/**
 * 3D Model content type — GLB/glTF viewer (EAA-1642).
 */

import type { ContentConfig } from '@ai-content-hub/content-core';

export type Model3DFormat = 'glb' | 'gltf';

export type LightPreset = 'studio' | 'outdoor' | 'soft' | 'neutral';

/**
 * EAA-1642: author-defined click target — clicking `objectName` in the player
 * plays `clipName`. The names are the raw glTF node / AnimationClip names, so
 * a mapping survives a re-upload of the same model.
 */
export interface Model3DObjectInteraction {
    objectName: string;
    clipName: string;
}

export interface Content3DData {
    title?: string;
    description?: string;
    /** UUID of the Content item — used to mint one-time serve URLs at play time. */
    contentId?: string;
    /** Model3DAsset id returned by the upload endpoint. */
    assetId?: string;
    fileFormat?: Model3DFormat;
    originalFilename?: string;
    /** Optional viewer hints. */
    autoRotate?: boolean;
    background?: string;
    /** Auto-play embedded animations when the model loads (default: false). */
    autoPlayAnimation?: boolean;
    /** Lighting preset for the 3D viewer (default: 'studio'). */
    lightPreset?: LightPreset;
    /** Model3DAsset id for a custom HDR environment map (overrides lightPreset). */
    environmentAssetId?: string;
    /** Original filename of the uploaded environment map. */
    environmentFilename?: string;
    /** Completion gate: minimum seconds the learner must view before "Mark done" (0 = off). */
    minViewSeconds?: number;
    /**
     * EAA-1642: outline of the uploaded model, cached at edit time.
     * The player renders its clip buttons from `animationNames` so the bar is
     * laid out before the (potentially large) GLB finishes downloading.
     */
    animationNames?: string[];
    /** Clickable object names discovered in the model, cached at edit time. */
    objectNames?: string[];
    /** Show one button per animation clip below the 3D view (default: true). */
    showAnimationButtons?: boolean;
    /**
     * Show the animation timeline (play/pause, scrubber, time) over the canvas.
     * Default false — the timeline is HIDDEN; the author opts in.
     * Positive naming: ticking the box enables the thing it names, and the
     * default is still the falsy value.
     */
    showAnimationTimeline?: boolean;
    /**
     * Repeat each clip instead of playing it once and stopping.
     * Default false — a clip plays ONCE. Named "loop" rather than "once" so
     * the default stays the falsy value: a missing key degrades to the
     * intended behaviour rather than the opposite of it.
     */
    loopAnimation?: boolean;
    /** Click-an-object → play-a-clip mappings authored in the editor. */
    objectInteractions?: Model3DObjectInteraction[];
}

export type Content3DConfig = ContentConfig;

export interface Content3DContent {
    id: string;
    type: '3d';
    version: string;
    data: Content3DData;
    config?: Content3DConfig;
}

export type Content3DAnswer = {
    completed: boolean;
    viewedSeconds?: number;
};

export const defaultContent3DI18n = {
    en: {
        contentTab: 'Model',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: '3D model title',
        descLabel: 'Instructions (optional)',
        descPlaceholder: 'Short instructions',
        uploadDropzone: 'Drag & drop a .glb / .gltf file, or click to browse',
        uploadHint: 'Supported: GLB, glTF',
        uploadOnlyGlb: 'Only .glb or .gltf files are supported.',
        uploadTooLarge: 'The model is larger than the {max}MB limit.',
        uploadUploading: 'Uploading…',
        uploadReplace: 'Replace model',
        uploadDelete: 'Remove',
        loading: 'Loading 3D model…',
        loadError: 'Could not load the 3D model.',
        retry: 'Retry',
        markDone: 'Mark done',
        completed: 'Completed',
        dwellHint: 'Explore the model for at least {{seconds}}s to continue.',
        autoRotateLabel: 'Auto-rotate the model',
        autoPlayAnimationLabel: 'Auto-play animations',
        lightPresetLabel: 'Lighting',
        lightPresetStudio: 'Studio (bright)',
        lightPresetOutdoor: 'Outdoor (sunlight)',
        lightPresetSoft: 'Soft (even)',
        lightPresetNeutral: 'Neutral',
        lightPresetCustom: 'Custom (HDR)',
        envUploadLabel: 'Environment Map',
        envUploadDropzone: 'Upload .hdr environment map',
        envUploadHint: 'Supported: HDR (Radiance)',
        envUploading: 'Uploading…',
        envRemove: 'Remove',
        minViewSecondsLabel: 'Minimum viewing time (seconds, 0 = off)',
        animationsLabel: 'Animations',
        animationsNone: 'This model has no embedded animations.',
        animationsScan: 'Scan model for animations & objects',
        animationsScanning: 'Reading the model\u2026',
        animationsRescan: 'Rescan model',
        animationsScanError: 'Could not read the model structure.',
        animationsScanHint: 'Downloads the model once to read its clip and object names.',
        showAnimationButtonsLabel: 'Show an animation button per clip below the view',
        showAnimationTimelineLabel: 'Show the animation timeline over the view',
        loopAnimationLabel: 'Replay the animation continuously',
        replayAnimation: 'Replay',
        interactionsLabel: 'Object interactions',
        interactionsHint: 'Pick an animation for an object; clicking that object in the player plays it.',
        interactionsSearch: 'Search objects\u2026',
        interactionsNone: '\u2014 No animation \u2014',
        interactionsEmpty: 'No objects match the search.',
        interactionsAssigned: 'Assigned ({{count}})',
        interactionsShowing: 'Showing {{shown}} of {{total}} objects \u2014 refine the search to see more.',
        interactionsParts: '{{count}} parts',
        interactionsHighlight: 'Click to highlight in the preview',
        interactionsObjectsNone: 'No clickable objects found in this model.',
        stopAnimation: 'Stop',
    },
    vi: {
        contentTab: 'Mô hình',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Tiêu đề mô hình 3D',
        descLabel: 'Hướng dẫn (tuỳ chọn)',
        descPlaceholder: 'Hướng dẫn ngắn gọn',
        uploadDropzone: 'Kéo thả tệp .glb / .gltf, hoặc bấm để chọn',
        uploadHint: 'Hỗ trợ: GLB, glTF',
        uploadOnlyGlb: 'Chỉ hỗ trợ tệp .glb hoặc .gltf.',
        uploadTooLarge: 'Mô hình vượt quá giới hạn {max}MB.',
        uploadUploading: 'Đang tải lên…',
        uploadReplace: 'Thay mô hình',
        uploadDelete: 'Xoá',
        loading: 'Đang tải mô hình 3D…',
        loadError: 'Không thể tải mô hình 3D.',
        retry: 'Thử lại',
        markDone: 'Hoàn thành',
        completed: 'Đã hoàn thành',
        dwellHint: 'Khám phá mô hình ít nhất {{seconds}} giây để tiếp tục.',
        autoRotateLabel: 'Tự động xoay mô hình',
        autoPlayAnimationLabel: 'Tự động phát hoạt ảnh',
        lightPresetLabel: 'Ánh sáng',
        lightPresetStudio: 'Studio (sáng)',
        lightPresetOutdoor: 'Ngoài trời (ánh nắng)',
        lightPresetSoft: 'Dịu (đều)',
        lightPresetNeutral: 'Trung tính',
        lightPresetCustom: 'Tuỳ chỉnh (HDR)',
        envUploadLabel: 'Bản đồ môi trường',
        envUploadDropzone: 'Tải lên tệp .hdr',
        envUploadHint: 'Hỗ trợ: HDR (Radiance)',
        envUploading: 'Đang tải…',
        envRemove: 'Xoá',
        minViewSecondsLabel: 'Thời gian xem tối thiểu (giây, 0 = tắt)',
        animationsLabel: 'Hoạt ảnh',
        animationsNone: 'Mô hình này không có hoạt ảnh nào.',
        animationsScan: 'Quét hoạt ảnh và đối tượng của mô hình',
        animationsScanning: 'Đang đọc mô hình\u2026',
        animationsRescan: 'Quét lại mô hình',
        animationsScanError: 'Không thể đọc cấu trúc mô hình.',
        animationsScanHint: 'Tải mô hình một lần để đọc tên hoạt ảnh và tên đối tượng.',
        showAnimationButtonsLabel: 'Hiện nút hoạt ảnh cho từng đoạn bên dưới khung xem',
        showAnimationTimelineLabel: 'Hiện thanh thời gian hoạt ảnh trên khung xem',
        loopAnimationLabel: 'Phát lại hoạt ảnh liên tục',
        replayAnimation: 'Phát lại',
        interactionsLabel: 'Tương tác với đối tượng',
        interactionsHint: 'Chọn hoạt ảnh cho một đối tượng; người học bấm vào đối tượng đó sẽ phát hoạt ảnh.',
        interactionsSearch: 'Tìm đối tượng\u2026',
        interactionsNone: '\u2014 Không có hoạt ảnh \u2014',
        interactionsEmpty: 'Không có đối tượng nào khớp với từ khoá.',
        interactionsAssigned: 'Đã gán ({{count}})',
        interactionsShowing: 'Hiển thị {{shown}}/{{total}} đối tượng \u2014 thu hẹp từ khoá để xem thêm.',
        interactionsParts: '{{count}} bộ phận',
        interactionsHighlight: 'Bấm để làm nổi bật trong khung xem trước',
        interactionsObjectsNone: 'Không tìm thấy đối tượng có thể bấm trong mô hình này.',
        stopAnimation: 'Dừng',
    },
} as const;

export const defaultContent3DConfig: Content3DConfig = {
    behavior: {
        allowRetry: true,
        maxAttempts: 0,
        shuffleChoices: false,
        showCorrectAnswer: false,
        showExplanation: false,
        showHint: false,
        instantFeedback: false,
        examMode: false,
        autoAdvance: false,
    },
    scoring: {
        max: 1,
        partialCreditEnabled: false,
        penaltyPerWrongAnswer: 0,
        passingScore: 1,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultContent3DI18n,
    },
};

export const defaultContent3DData: Content3DData = {
    title: '',
    description: '',
    assetId: '',
    // EAA-1642: both default to OFF. A model now ships explicit animation
    // buttons and click-to-play object triggers, so the learner starts the
    // motion — auto-rotate and auto-play fought that (and the camera) instead.
    autoRotate: false,
    autoPlayAnimation: false,
    lightPreset: 'studio',
    showAnimationButtons: true,
    showAnimationTimeline: false,
    loopAnimation: false,
    objectInteractions: [],
};

export function hasConfigured3DModel(data: Content3DData | undefined): boolean {
    return Boolean(data?.assetId && (data.assetId ?? '').trim());
}

export const content3DSchema = {
    type: '3d' as const,
    title: '3D Model',
    description: 'Interactive 3D model (GLB/glTF) rendered with three.js',
    version: '1.0.0',
    category: 'layout' as const,
    icon: 'Box',
    tags: ['3d', 'model', 'glb', 'gltf', 'viewer', 'threejs'],
    defaultData: defaultContent3DData,
    defaultConfig: defaultContent3DConfig,
};
